import { VStack, Stack, Box, Hidden, Text } from "native-base";
import dayjs from "dayjs";
import { formatDateToISOWithUTCOffset, numberWithDots } from "utils/helpers";
import FormContainer from "atoms/FormContainer";
import { AiOutlineSolution } from "react-icons/ai";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import FormMenu from "atoms/FormMenu";
import ValidatedInput from "atoms/ValidatedInput";
import InfoBox from "atoms/InfoBox";
import { STATUS_OPTIONS, TYPE_OPTIONS } from "global/constants";
import { useTranslation } from "react-i18next";
import AmplitudeHelper from "utils/analytics";
import SalarySection from "molecules/SalarySection";
import GroupAndPayDateRender from "molecules/GroupAndPayDateRender";
import colors from "theme/colors";
import tenantConfig from "atoms/TextCurrency/tenantConfig";

function renderEmployeeCode(formikErrors, errors, hasPendingUpdateRequest, values, handleChange) {
  return (
    <ValidatedInput
      testID="viEmpId"
      label="employeeId"
      errors={{ ...formikErrors, ...errors }}
      title="employeeCreation.single.employeeId"
      required
      disabled={hasPendingUpdateRequest}
      value={values?.employeeId ?? ""}
      setValue={handleChange("employeeId")}
      amplitude="employee_code"
    />
  );
}

function renderEmployeeType(formikErrors, errors, hasPendingUpdateRequest, values, t, setFieldValue) {
  return (
    <ValidatedInput
      label="employeeType"
      errors={{ ...formikErrors, ...errors }}
      title="employeeCreation.single.employeeType"
      component={
        <FormMenu
          data={TYPE_OPTIONS}
          testID="viEmpType"
          isDisabled={hasPendingUpdateRequest}
          value={values?.employeeType?.data ? t(`home.list.rows.${values?.employeeType?.data}`) : ""}
          setValue={(x) => setFieldValue("employeeType", x)}
          isError={Object.prototype.hasOwnProperty.call({ ...formikErrors, ...errors }, "employeeType")}
          amplitude="employee_type"
        />
      }
      required
    />
  );
}

function renderEmpBankName(formikErrors, errors, banksList, hasPendingUpdateRequest, values, setFieldValue) {
  return (
    <ValidatedInput
      label="bankName"
      errors={{ ...formikErrors, ...errors }}
      title="employeeCreation.single.bankName"
      component={
        <FormMenu
          testID="viBankName"
          data={banksList}
          isDisabled={hasPendingUpdateRequest}
          value={values?.bankName?.bankName ?? ""}
          setValue={(x) => setFieldValue("bankName", x)}
          isError={Object.prototype.hasOwnProperty.call({ ...formikErrors, ...errors }, "bankName")}
          amplitude="bank_name"
        />
      }
      required
    />
  );
}

function renderEmpBeneficiaryName(formikErrors, errors, hasPendingUpdateRequest, values, handleChange) {
  return (
    <ValidatedInput
      testID="viEmpBeneficiaryName"
      label="beneficiaryName"
      errors={{ ...formikErrors, ...errors }}
      disabled={hasPendingUpdateRequest}
      title="employeeCreation.single.beneficiaryName"
      value={values?.beneficiaryName ?? ""}
      setValue={handleChange("beneficiaryName")}
      required
      amplitude="beneficiary_name"
    />
  );
}

function renderEmpBankNumber(formikErrors, errors, hasPendingUpdateRequest, values, handleChange) {
  return (
    <ValidatedInput
      testID="viEmpBankNum"
      label="bankNumber"
      errors={{ ...formikErrors, ...errors }}
      title="employeeCreation.single.bankNumber"
      required
      disabled={hasPendingUpdateRequest}
      value={values?.bankNumber ?? ""}
      setValue={handleChange("bankNumber")}
      amplitude="bank_account_number"
    />
  );
}

function renderEmpSalType(formikErrors, errors) {
  return (
    <ValidatedInput
      testID="viEmpSalType"
      errors={{ ...formikErrors, ...errors }}
      title="employeeCreation.single.salaryType"
      label="salaryType"
      disabled
      value="Bulanan"
    />
  );
}

function shouldRenderWithdrawalAmount(withdrawalLimit) {
  const { currencySymbol } = tenantConfig.id;
  if (withdrawalLimit) return `${currencySymbol}${numberWithDots(withdrawalLimit)}`;
  return null;
}

function renderMaxWithdrawalInput(formikErrors, errors, values, t) {
  return (
    <Box>
      <ValidatedInput
        testID="viEpSalType"
        title="employeeCreation.single.maxWithdrawal"
        errors={{ ...formikErrors, ...errors }}
        label="salaryType"
        disabled
        value={shouldRenderWithdrawalAmount(values?.withdrawalLimit)} // TODO : faormation
      />

      <Text variant="xs" color={colors.secondary.orchid}>
        {t("employeeForm.withdrawalNotify")}
      </Text>
    </Box>
  );
}

function renderEmpSalary(formikErrors, errors, isUpdate, hasPendingUpdateRequest, values, handleChange) {
  return (
    <ValidatedInput
      errors={{ ...formikErrors, ...errors }}
      title="employeeCreation.single.salary"
      label="salary"
      required
      component={
        <SalarySection
          testID="viEmpSalary"
          isUpdate={isUpdate}
          disabled={hasPendingUpdateRequest}
          salary={values?.salary ?? ""}
          setSalary={handleChange("salary")}
        />
      }
    />
  );
}

function renderEmpStatus(
  formikErrors,
  errors,
  isUpdate,
  hasPendingUpdateRequest,
  isRowDisabled,
  values,
  t,
  setFieldValue,
) {
  return (
    <ValidatedInput
      label="status"
      errors={{ ...formikErrors, ...errors }}
      title="employeeCreation.single.status"
      required
      component={
        <FormMenu
          testID="viEmpStatus"
          data={STATUS_OPTIONS}
          isUpdate={isUpdate}
          shouldShowCheckIcon
          isDisabled={hasPendingUpdateRequest || isRowDisabled}
          value={values?.status ? t(`home.list.rows.${values?.status}`) : ""}
          setValue={(x) => {
            setFieldValue("status", x?.data);
          }}
          isError={Object.prototype.hasOwnProperty.call({ ...formikErrors, ...errors }, "status")}
          amplitude="employee_status"
        />
      }
    />
  );
}

function renderEmpDOJ(formikErrors, errors, values, hasPendingUpdateRequest, setFieldValue) {
  return (
    <ValidatedInput
      label="dateJoined"
      errors={{ ...formikErrors, ...errors }}
      title="employeeCreation.single.dateJoined"
      required
      component={
        <>
          <Hidden till="lg">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                testID="viEmpDOJ"
                views={["year", "month", "day"]}
                disableFuture
                value={values?.dateJoined ? dayjs(values?.dateJoined) : ""}
                disabled={hasPendingUpdateRequest}
                onChange={(x) => {
                  const selectedDate = new Date(x);
                  AmplitudeHelper.logEvent("date_joined_filled", {
                    source: "single_employee_creation_page",
                    input: formatDateToISOWithUTCOffset(selectedDate),
                  });
                  setFieldValue("dateJoined", Date.parse(dayjs(x)));
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    error: Object.prototype.hasOwnProperty.call({ ...formikErrors, ...errors }, "dateJoined"),
                  },
                }}
                renderInput={(params) => <TextField {...params} helperText={null} />}
              />
            </LocalizationProvider>
          </Hidden>
          <Hidden from="lg">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                testID="viEmpDOJ"
                disabled={hasPendingUpdateRequest}
                disableFuture
                views={["year", "month", "day"]}
                value={values?.dateJoined ? dayjs(values?.dateJoined) : ""}
                disableOpenPicker={hasPendingUpdateRequest}
                onChange={(x) => {
                  const selectedDate = new Date(x);
                  AmplitudeHelper.logEvent("date_joined_filled", {
                    source: "single_employee_creation_page",
                    input: formatDateToISOWithUTCOffset(selectedDate),
                  });
                  setFieldValue("dateJoined", Date.parse(dayjs(x)));
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    error: Object.prototype.hasOwnProperty.call({ ...formikErrors, ...errors }, "dateJoined"),
                  },
                }}
                renderInput={(params) => <TextField {...params} helperText={null} />}
              />
            </LocalizationProvider>
          </Hidden>
        </>
      }
    />
  );
}

function renderEmpSuspensionCategory(
  values,
  formikErrors,
  errors,
  categoriesList,
  isUpdate,
  hasPendingUpdateRequest,
  isRowDisabled,
  t,
  setFieldValue,
) {
  return (
    values?.status &&
    values?.status !== STATUS_OPTIONS[0].data && (
      <ValidatedInput
        label="suspensionCategory"
        errors={{ ...formikErrors, ...errors }}
        title="employeeCreation.single.reason"
        required
        component={
          <FormMenu
            data={categoriesList}
            testID="viEmpSuspCat"
            isUpdate={isUpdate}
            isDisabled={hasPendingUpdateRequest || isRowDisabled}
            placeholder={t("employeeCreation.single.suspensionCategoryPlaceholder")}
            value={values?.suspensionCategory ? t(`home.statusReason.${values?.suspensionCategory}`) : ""}
            setValue={(x) => {
              setFieldValue("suspensionCategory", x?.categoryName);
            }}
            isError={Object.prototype.hasOwnProperty.call({ ...formikErrors, ...errors }, "suspensionCategory")}
            amplitude="suspension_category"
          />
        }
      />
    )
  );
}

function renderEmpSuspensionReason(
  values,
  t,
  hasPendingUpdateRequest,
  isRowDisabled,
  formikErrors,
  errors,
  handleChange,
) {
  return (
    values?.suspensionCategory &&
    values?.suspensionCategory.includes("Other") &&
    values?.status !== STATUS_OPTIONS[0].data && (
      <>
        <ValidatedInput
          testID="viEmpSuspReason"
          label="suspendedReason"
          placeholder={t("employeeCreation.single.suspendedReasonPlaceholder")}
          disabled={hasPendingUpdateRequest || isRowDisabled}
          shouldShowLength
          maxLength={100}
          errors={{ ...formikErrors, ...errors }}
          title="employeeCreation.single.chooseDetailedReason"
          value={values?.suspendedReason ?? ""}
          setValue={handleChange("suspendedReason")}
          amplitude="suspended_reason"
        />
      </>
    )
  );
}

const EmployeeForm = ({
  errors = null,
  banksList = [],
  formikProps = {},
  isUpdate = false,
  hasPendingUpdateRequest = false,
  isRowDisabled = false,
  categoriesList = [],
  groupPayDates = {},
}) => {
  const { t } = useTranslation("common");
  const direction = ["column", "column", "row", "row", "row"];
  const flex = [null, null, null, 0.5, 0.5];
  const space = [5, 5, 5, 10, 10];

  const {
    handleChange = () => {},
    setFieldValue = () => {},
    setValues = () => {},
    values = {},
    errors: formikErrors = {},
  } = formikProps;

  return (
    <FormContainer
      title="employeeCreation.single.employeeData"
      description="employeeCreation.single.employeeDataDesc"
      icon={<AiOutlineSolution size={25} />}
    >
      <Stack direction={direction} space={space}>
        <VStack flex={flex} space={3} maxW={{ base: "100%", md: "50%" }}>
          {renderEmployeeCode(formikErrors, errors, hasPendingUpdateRequest, values, handleChange)}
          {renderEmployeeType(formikErrors, errors, hasPendingUpdateRequest, values, t, setFieldValue)}
          {renderEmpBankName(formikErrors, errors, banksList, hasPendingUpdateRequest, values, setFieldValue)}
          {renderEmpBeneficiaryName(formikErrors, errors, hasPendingUpdateRequest, values, handleChange)}
          {renderEmpBankNumber(formikErrors, errors, hasPendingUpdateRequest, values, handleChange)}
          {renderEmpSalType(formikErrors, errors)}
          {renderEmpSalary(formikErrors, errors, isUpdate, hasPendingUpdateRequest, values, handleChange)}
          {renderMaxWithdrawalInput(formikErrors, errors, values, t)}
        </VStack>
        <VStack flex={flex} space={3} minW="50%">
          {renderEmpDOJ(formikErrors, errors, values, hasPendingUpdateRequest, setFieldValue)}
          <GroupAndPayDateRender
            formikErrors={formikErrors}
            errors={errors}
            values={values}
            hasPendingUpdateRequest={hasPendingUpdateRequest}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            setValues={setValues}
            isUpdate={isUpdate}
            groupPayDates={groupPayDates}
          />

          {renderEmpStatus(
            formikErrors,
            errors,
            isUpdate,
            hasPendingUpdateRequest,
            isRowDisabled,
            values,
            t,
            setFieldValue,
          )}

          {renderEmpSuspensionCategory(
            values,
            formikErrors,
            errors,
            categoriesList,
            isUpdate,
            hasPendingUpdateRequest,
            isRowDisabled,
            t,
            setFieldValue,
          )}
          {renderEmpSuspensionReason(
            values,
            t,
            hasPendingUpdateRequest,
            isRowDisabled,
            formikErrors,
            errors,
            handleChange,
          )}
        </VStack>
      </Stack>
      {hasPendingUpdateRequest && (
        <Box mt="32px">
          <InfoBox
            warning
            shouldShowDescription
            title="employeeUpdation.single.warningTitle"
            description="employeeUpdation.single.WarningDescription"
          />
        </Box>
      )}
    </FormContainer>
  );
};

export default EmployeeForm;
